import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SequenceNameQuery, SequenceNameQueryVariables} from 'molbio/__generated/SequenceNameQuery';
import {SchemaValidationShortDisplayTextByStatus, ValidationExtras} from 'schemas/SchemaValidationConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY, GREEN, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {
  BulkUnlinkSpec,
  CategoricalUnlinkSpec,
  PartsOrPartLinks,
  SequencesById,
} from './BulkUnlinkPartsConstants';

export const SEQUENCE_NAME_QUERY = gql`
  query SequenceNameQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    sequence(id: $id) {
      ... on SequenceNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BulkUnlinkPartsTableProps {
  partsOrPartLinksToRemove: Array<PartsOrPartLinks>;
  selectedField: BulkUnlinkSpec;
  sequencesById: SequencesById;
  validationExtrasBySequenceId: {
    [id: string]: ValidationExtras;
  };
}

export default function BulkUnlinkPartsTable({
  partsOrPartLinksToRemove,
  selectedField,
  sequencesById,
  validationExtrasBySequenceId,
}: BulkUnlinkPartsTableProps): JSX.Element {
  return (
    <Table
      numRows={partsOrPartLinksToRemove.length}
      emptyTableText={
        selectedField === CategoricalUnlinkSpec.ORPHANED ? 'No parts found.' : 'No part links found.'
      }
    >
      <Column
        header={selectedField === CategoricalUnlinkSpec.ORPHANED ? 'Part' : 'Part Links in Schema Field'}
        renderCell={(rowIndex: number) => (
          <Cell>
            {partsOrPartLinksToRemove[rowIndex].partSequenceIds.map((partSequenceId) => (
              <span className={css(styles.partLinksColumn)} key={partSequenceId}>
                <BenchlingIcon icon={FolderItemIcon.Sequence} color={GRAY} className={css(styles.icon)} />{' '}
                <BenchlingQuery<SequenceNameQuery, SequenceNameQueryVariables>
                  query={SEQUENCE_NAME_QUERY}
                  variables={{id: partSequenceId}}
                  renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
                >
                  {(data) => (!isInaccessible(data.sequence) ? data.sequence.name : 'Not found.')}
                </BenchlingQuery>
                <br />
              </span>
            ))}
          </Cell>
        )}
      />
      {selectedField !== CategoricalUnlinkSpec.ORPHANED && (
        <Column
          header="Validation Status"
          renderCell={(rowIndex: number) =>
            partsOrPartLinksToRemove[rowIndex].validationError ? (
              <Cell className={css(styles.invalid)}>Invalid</Cell>
            ) : (
              <Cell className={css(styles.valid)}>Valid</Cell>
            )
          }
        />
      )}
      <Column
        header="Sequence"
        renderCell={(rowIndex: number) => {
          const sequence = sequencesById[partsOrPartLinksToRemove[rowIndex].containerSequenceId];
          return (
            <Cell>
              {sequence.name}
              {validationExtrasBySequenceId[sequence.id].errorRecords &&
                validationExtrasBySequenceId[sequence.id].errorRecords!.length > 0 && (
                  <Tooltip
                    title={validationExtrasBySequenceId[sequence.id]
                      .errorRecords!.map((errorRecord) => errorRecord.message)
                      .join('\n')}
                  >
                    <span className={css(styles.invalid)}>
                      {' '}
                      {SchemaValidationShortDisplayTextByStatus.FAILED}
                    </span>
                  </Tooltip>
                )}
            </Cell>
          );
        }}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  partLinksColumn: {
    marginBottom: '0px',
    marginTop: '3px',
  },
  icon: {
    paddingTop: '3px',
  },
  invalid: {
    color: RED,
  },
  valid: {
    color: GREEN,
  },
});
